// Sidebar.jsx
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router';
import { Drawer, Box, Typography, IconButton } from '@mui/material';
import {
	KeyboardDoubleArrowLeft as KeyboardDoubleArrowLeftIcon,
	KeyboardDoubleArrowRight as KeyboardDoubleArrowRightIcon,
} from '@mui/icons-material';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '../../hooks/useMediaQuery';

// Import the modular components
import ContactsSection from './ContactsSection';
import AccountDetails from './AccountDetails';
import AppSelector from './AppSelector';
import HubNavigation from './HubNavigation';
import InvestingNavigation from './InvestingNavigation';

/**
 * Sidebar component for the application
 * @param {Object} tenant - The tenant object containing information and contact users
 * @param {Object} user - The current user object
 */
const Sidebar = ({ tenant, user }) => {
	const theme = useTheme();
	const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
	const location = useLocation();

	// Determine which app is currently active
	const isInvestingApp = location.pathname.includes('/apps/investing');
	const currentApp = isInvestingApp ? 'investing' : 'hub';

	// Define breakpoints for different device sizes
	const isLaptop = useMediaQuery('(max-width: 1200px)');
	const isTablet = useMediaQuery('(max-width: 900px)');
	const isMobile = useMediaQuery('(max-width: 600px)');

	// Load saved preference from localStorage or default to collapsed on laptops
	useEffect(() => {
		const savedState = localStorage.getItem('sidebarCollapsed');
		if (savedState !== null) {
			setIsSidebarCollapsed(JSON.parse(savedState));
		} else {
			setIsSidebarCollapsed(isLaptop);
		}
	}, [isLaptop]);

	// Save preference to localStorage when it changes
	useEffect(() => {
		localStorage.setItem(
			'sidebarCollapsed',
			JSON.stringify(isSidebarCollapsed)
		);
		// Dispatch custom event to notify other components
		window.dispatchEvent(new Event('sidebarStateChange'));
	}, [isSidebarCollapsed]);

	const toggleSidebar = () => {
		setIsSidebarCollapsed(!isSidebarCollapsed);
	};

	// Calculate drawer width based on collapsed state and screen size
	const collapsedWidth = 80; // Consistent collapsed width
	let expandedWidth;

	// Set expanded width based on screen size
	if (isMobile) {
		expandedWidth = 240; // Smaller for mobile (xs, sm)
	} else if (isTablet) {
		expandedWidth = 260; // Medium for tablets (md)
	} else if (isLaptop) {
		expandedWidth = 280; // Default for laptops (lg)
	} else {
		expandedWidth = 320; // Larger for desktops (xl)
	}

	const drawerWidth = isSidebarCollapsed ? collapsedWidth : expandedWidth;

	return (
		<Drawer
			variant='permanent'
			sx={{
				width: drawerWidth,
				flexShrink: 0,
				[`& .MuiDrawer-paper`]: {
					width: drawerWidth,
					boxSizing: 'border-box',
					backgroundColor: theme.palette.background.default,
					display: 'flex',
					flexDirection: 'column',
					paddingBottom: '70px',
					borderRadius: '0 0 8px 0', // Round only the bottom-right corner
					transition: theme.transitions.create('width', {
						easing: theme.transitions.easing.sharp,
						duration: theme.transitions.duration.enteringScreen,
					}),
					overflowX: 'hidden',
				},
			}}
		>
			{/* Fixed Logo Section with Toggle Button */}
			<Box
				sx={{
					backgroundColor: theme.palette.background.paper,
					padding: isSidebarCollapsed ? 1 : '12px 16px',
					display: 'flex',
					justifyContent: isSidebarCollapsed ? 'center' : 'space-between',
					alignItems: 'center',
					height: '48px', // Fixed height for consistent alignment
				}}
			>
				{isSidebarCollapsed ? (
					<IconButton onClick={toggleSidebar} size='medium'>
						<KeyboardDoubleArrowRightIcon />
					</IconButton>
				) : (
					<>
						<Typography
							variant='h4'
							noWrap
							component='div'
							gutterBottom={false}
							paragraph={false}
							sx={{
								lineHeight: 1.5,
								margin: 0,
								alignSelf: 'center',
							}}
						>
							{tenant.name}
						</Typography>
						<IconButton
							onClick={toggleSidebar}
							size='medium'
							color='inherit'
							sx={{ padding: 0 }}
						>
							<KeyboardDoubleArrowLeftIcon />
						</IconButton>
					</>
				)}
			</Box>

			{/* Scrollable Content Area */}
			<Box sx={{ flexGrow: 1, overflow: 'auto' }}>
				{/* App Selector */}
				<AppSelector user={user} isCollapsed={isSidebarCollapsed} />

				{/* App-specific Navigation Menu */}
				{currentApp === 'hub' ? (
					<HubNavigation
						tenant={tenant}
						user={user}
						isCollapsed={isSidebarCollapsed}
					/>
				) : (
					<InvestingNavigation user={user} isCollapsed={isSidebarCollapsed} />
				)}

				{/* Contact Cards - Only show when sidebar is expanded and in Hub app */}
				{!isSidebarCollapsed && currentApp === 'hub' && (
					<ContactsSection contacts={tenant} />
				)}
			</Box>

			{/* Fixed Account Details Footer */}
			<AccountDetails
				tenant={tenant}
				user={user}
				isCollapsed={isSidebarCollapsed}
				onToggleSidebar={toggleSidebar}
			/>
		</Drawer>
	);
};

export default Sidebar;
